import {
  AnchoredPopup,
  LabelChip,
  SearchInput,
  type AnchoredPopupAnchor,
} from "@carbon6/c6-frontend-design";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type Ref,
} from "react";
import { normalizeWhitespace } from "@/utils";
import styles from "./FilterSearchCombobox.module.css";

/** 드롭다운에 표시하는 조건 후보 한 줄이다. `value` 는 후보 집합 안에서 유일해야 한다. */
export type FilterSearchCandidate = {
  value: string;
  // 제목("Quality: blur")이다. 매칭과 표시 모두 이 문구를 기준으로 한다.
  label: string;
  // 보조 설명(라벨 설명 또는 key)이다. 매칭에도 쓴다.
  description?: string;
  // 칩 색(0xRRGGBB)이다. 없으면 중립색을 쓴다.
  color?: number;
  // 같은 색의 hex 문자열이다. 고른 뒤 라벨 조건 토큰(c6 SearchToken)에 전달한다.
  accentColor?: string;
  // 오른쪽 끝의 건수다. 없으면 칸을 비운다.
  count?: number;
};

export type FilterSearchComboboxProps = {
  value: string;
  onValueChange: (value: string) => void;
  // 이름 검색을 확정한다. Enter(하이라이트된 후보가 없을 때)나 돋보기 버튼으로 호출한다.
  onSearch: (query: string) => void;
  candidates: readonly FilterSearchCandidate[];
  onSelectCandidate: (candidate: FilterSearchCandidate) => void;
  // 이미 적용한 후보다. 목록에서 뺀다. 같은 조건을 두 번 적용할 수 없고, 칩 행이 그 사실을 보여 준다.
  appliedValues?: ReadonlySet<string>;
  // 타이핑 중의 후보 섹션 머리말이다.
  sectionLabel?: string;
  // 빈 입력에 포커스했을 때의 머리말(추천 목록)이다.
  emptySectionLabel?: string;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  // 드롭다운에 표시하는 최대 행 수다. 넘치면 나머지를 "n개 더" 안내로 줄인다.
  maxVisible?: number;
  // 후보 카탈로그를 아직 받는 중이다. 이때 빈 목록을 "없음"으로 표시하지 않는다.
  loading?: boolean;
  loadingMessage?: string;
  // 후보 카탈로그를 받지 못했다. 값이 있으면 빈 목록 자리에 이 문구를 표시한다. 받지 못한 목록을
  // "없음"으로 표시하지 않는다. 소비처는 표시할 후보가 없을 때만 넘긴다.
  errorMessage?: string;
  emptyMessage?: string;
  // 입력 크기다. 생략하면 c6 기본값(md)이다. 필터 행은 생략한다(rules.md §17).
  inputSize?: "sm" | "md";
  // 메뉴 최소 폭(px)이다. 좁은 칸에 둘 때 줄인다.
  minMenuWidth?: number;
  invalid?: boolean;
  disabled?: boolean;
  // 입력 오른쪽의 ✕(지우기) 버튼이다. 좁은 칸에서는 끈다. 켜면 입력 칸이 그만큼 좁아진다.
  showClear?: boolean;
  // 입력 요소를 받는다. 부모가 다른 칸에서 이 칸으로 포커스를 옮길 때 쓴다.
  inputRef?: Ref<HTMLInputElement>;
};

const DEFAULT_MAX_VISIBLE = 8;
// 메뉴 최소 폭이다. 칩·설명·건수가 한 줄에 들어가야 한다. 입력이 이보다 넓으면 입력 폭을 따른다.
const MIN_MENU_WIDTH_PX = 560;

/**
 * 이름 검색과 조건 후보 선택을 한 입력에서 하는 콤보박스다.
 *
 * 입력값은 이름 검색 초안이다(부모가 debounce 와 커밋을 소유한다). 포커스하면 후보 목록을 표시한다:
 * - 빈 입력이면 부모가 전달한 순서대로 앞에서부터 표시한다(추천).
 * - 타이핑하면 매칭되는 후보만 같은 순서로 표시한다.
 * 후보를 고르면 `onSelectCandidate` 를 호출한다. 입력을 비울지는 부모가 정한다(데이터셋 검색은
 * 비운다). Enter 는 하이라이트된 후보가 있으면 그 후보를 고르고, 없으면 이름 검색을 한다. 타이핑
 * 문법(`key:value`)은 해석하지 않는다.
 *
 * 입력 크롬은 c6 `SearchInput`(plain 모드. 돋보기·✕ 가 오른쪽 안쪽)을 그대로 쓴다. 메뉴만 c6
 * `AnchoredPopup` 에 표시한다. c6 `SearchInput` 의 자체 자동완성은 단순한 한 줄 옵션만 지원한다.
 * 그래서 섹션·건수·로딩 상태를 표시할 수 없다. 바깥 클릭 닫기는 포커스 이탈로 판정한다.
 */
export function FilterSearchCombobox({
  value,
  onValueChange,
  onSearch,
  candidates,
  onSelectCandidate,
  appliedValues,
  sectionLabel = "라벨 조건",
  emptySectionLabel = "추천 라벨",
  placeholder,
  ariaLabel = "검색",
  className,
  maxVisible = DEFAULT_MAX_VISIBLE,
  loading = false,
  loadingMessage = "조건 목록을 불러오는 중…",
  errorMessage,
  emptyMessage = "일치하는 조건이 없습니다.",
  inputSize,
  minMenuWidth = MIN_MENU_WIDTH_PX,
  invalid,
  disabled,
  showClear = true,
  inputRef,
}: FilterSearchComboboxProps): ReactNode {
  const listboxId = useId();
  const shellRef = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<AnchoredPopupAnchor | null>(null);
  const [menuWidth, setMenuWidth] = useState(MIN_MENU_WIDTH_PX);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const query = normalizeWhitespace(value);
  const matches = useMemo(
    () => matchCandidates(candidates, query, appliedValues),
    [candidates, query, appliedValues],
  );
  const visible = matches.slice(0, maxVisible);
  const hiddenCount = matches.length - visible.length;

  // 메뉴는 포커스하거나 타이핑하면 표시한다. 빈 입력에서도 추천(부모가 정한 순서의 앞부분)을 보여 준다.
  const openMenu = useCallback(() => {
    const shell = shellRef.current;
    if (shell === null) {
      return;
    }
    const rect = shell.getBoundingClientRect();
    setAnchor({ type: "rect", rect, el: shell });
    setMenuWidth(Math.max(rect.width, minMenuWidth));
    setOpen(true);
  }, [minMenuWidth]);
  const closeMenu = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  // 후보 목록이 줄어 하이라이트가 범위를 벗어나면 하이라이트를 해제한다.
  useEffect(() => {
    if (activeIndex >= visible.length) {
      setActiveIndex(visible.length - 1);
    }
  }, [activeIndex, visible.length]);

  const select = (candidate: FilterSearchCandidate) => {
    onSelectCandidate(candidate);
    setActiveIndex(-1);
  };

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    // 조합 확정 Enter 는 isComposing=false 여도 keyCode=229 일 수 있다. 후보 선택은
    // SearchInput 의 IME 검사보다 먼저 실행되므로 여기서도 두 신호를 함께 확인한다.
    if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) {
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        openMenu();
      }
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((current) => {
        if (visible.length === 0) {
          return -1;
        }
        return (current + step + visible.length) % visible.length;
      });
      return;
    }
    if (event.key === "Escape" && open) {
      event.preventDefault();
      // Esc 한 번은 메뉴만 닫아야 한다. 전파를 막지 않으면 부모 팝오버도 함께 닫힌다.
      event.stopPropagation();
      closeMenu();
      return;
    }
    if (event.key === "Enter" && open && activeIndex >= 0 && activeIndex < visible.length) {
      event.preventDefault();
      select(visible[activeIndex]);
    }
  };

  // 포커스가 셸 밖으로 나가면 닫는다. 후보 행은 mousedown 을 막아 포커스를 가져가지 않는다.
  // 그래서 행 클릭은 이 처리를 실행하지 않는다.
  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && shellRef.current?.contains(next)) {
      return;
    }
    closeMenu();
  };

  const activeId = activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined;

  return (
    <div
      ref={shellRef}
      className={[styles.shell, className ?? ""].filter(Boolean).join(" ")}
      role="search"
      onBlur={handleBlur}
    >
      {/* 크기는 지정하지 않는다. size 를 지정하지 않으면 c6 기본값(md)이고, 이것이 필터 행 1 의 규약이다(rules.md §17). */}
      <SearchInput
        ref={inputRef}
        inputSize={inputSize}
        invalid={invalid}
        disabled={disabled}
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={open ? activeId : undefined}
        showClear={showClear}
        onValueChange={(next) => {
          onValueChange(next);
          openMenu();
        }}
        onFocus={() => openMenu()}
        onKeyDown={handleKeyDown}
        onSearch={(submitted) => {
          closeMenu();
          onSearch(submitted);
        }}
      />
      <AnchoredPopup
        open={open}
        anchor={anchor}
        onClose={closeMenu}
        role="presentation"
        // c6 기본 max-width(28rem)는 입력보다 좁아서 행이 가로로 넘친다. 그래서 입력 폭과 최소 폭 중
        // 넓은 쪽으로 열고, 상한은 뷰포트만 둔다. 행 안의 넘침은 말줄임으로 처리한다.
        width={`${menuWidth}px`}
        maxWidth="calc(100vw - 1rem)"
        placement="bottom-start"
        offset={4}
        // 포커스는 입력에 남는다. presentation 역할의 페이지 레벨 팝업이라 c6 기본값도 포커스를
        // 옮기지 않는다. 그래서 닫힐 때 되돌릴 포커스도 없다.
        returnFocus={false}
        // 바깥 클릭 한 번으로 부모 팝오버도 닫혀야 한다. 포인터 닫기를 켜면 메뉴만 닫히고,
        // 부모 팝오버를 닫으려면 한 번 더 눌러야 한다. 메뉴 자체는 포커스 이탈로 닫는다.
        closeOnOutsidePointerDown={false}
        repositionOnScroll
        repositionOnResize
      >
        {/* c6 가 중첩 메뉴를 부모 팝오버의 내부로 판정한다. 후보를 눌러도 부모 팝오버는 닫히지
            않으므로 pointerdown 전파를 막지 않는다. */}
        <div className={styles.menu}>
          <div className={styles.sectionLabel} aria-hidden="true">
            {query === "" ? emptySectionLabel : sectionLabel}
          </div>
          {loading ? (
            <div className={styles.empty}>{loadingMessage}</div>
          ) : visible.length === 0 ? (
            <div className={styles.empty}>{errorMessage ?? emptyMessage}</div>
          ) : (
            <ul
              id={listboxId}
              role="listbox"
              aria-label={query === "" ? emptySectionLabel : sectionLabel}
              className={styles.list}
            >
              {visible.map((candidate, index) => (
                // 콤보박스 option 은 포커스를 받지 않는다. 키보드 조작은 입력의 ↑↓·Enter 가
                // aria-activedescendant 로 이 행을 가리켜 처리한다(WAI-ARIA combobox 패턴).
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <li
                  key={candidate.value}
                  id={`${listboxId}-${index}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  className={
                    index === activeIndex
                      ? `${styles.option} ${styles.optionActive}`
                      : styles.option
                  }
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => select(candidate)}
                >
                  {/* 라벨은 칩으로 표시한다. 목록 표·카드의 라벨 칩과 색과 모양이 같아서 고른 뒤에도
                      같은 모습이다. 칩은 자기 폭 안에서 말줄임한다(title 이 전체 문구다). 설명은
                      남는 폭에서 말줄임한다. */}
                  <span className={styles.chip}>
                    <LabelChip
                      color={candidate.color ?? 0}
                      size="xs"
                      variant="filled"
                      title={candidate.label}
                    >
                      {candidate.label}
                    </LabelChip>
                  </span>
                  {candidate.description ? (
                    <span className={styles.description} title={candidate.description}>
                      {candidate.description}
                    </span>
                  ) : null}
                  {candidate.count !== undefined ? (
                    <span className={styles.count}>{candidate.count.toLocaleString()}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {hiddenCount > 0 ? (
            <div className={styles.more}>
              {hiddenCount}개 더 —{" "}
              {query === "" ? "입력해 좁혀 주세요." : "계속 입력해 좁혀 주세요."}
            </div>
          ) : null}
        </div>
      </AnchoredPopup>
    </div>
  );
}

// 공백으로 나눈 모든 단어가 제목·설명·값 중 어딘가에 있으면 매칭이다(대소문자 무시). 그래서
// "quality blur" 처럼 key 와 value 를 띄어 써도, "blur" 만 써도 찾는다. 빈 질의는 전체를 반환한다
// (포커스 직후의 추천 목록).
export function matchCandidates(
  candidates: readonly FilterSearchCandidate[],
  query: string,
  appliedValues?: ReadonlySet<string>,
): readonly FilterSearchCandidate[] {
  const terms = query.toLowerCase().split(" ").filter(Boolean);
  return candidates.filter((candidate) => {
    if (appliedValues?.has(candidate.value)) {
      return false;
    }
    if (terms.length === 0) {
      return true;
    }
    const haystack =
      `${candidate.label} ${candidate.description ?? ""} ${candidate.value}`.toLowerCase();
    return terms.every((term) => haystack.includes(term));
  });
}
